import isString from 'lodash/isString';
import { cappedMemoize } from '../../utils/memoize';

// Reference: https://github.com/appsmithorg/appsmith/blob/release/app/client/src/utils/DynamicBindingUtils.ts
export const DATA_BIND_REGEX = /{{(?:[\s\S]*?)}}/;

export const DATA_BIND_NON_NESTED_REGEX = /{{\s*(?<temp1>[^{}]*)\s*}}/g;

export const DATA_BIND_GLOBAL_REGEX = new RegExp(DATA_BIND_REGEX.source, 'g');

export const EXTRACT_DATA_BIND_REGEX = /{{\s*(?<temp1>.*?)\s*}}/;

export function removeDataBindingBraces(str: string) {
  return str.replaceAll(DATA_BIND_GLOBAL_REGEX, (match) => match.slice(2, -2).trim());
}

const isDynamicValuePlain = (value: string): boolean => DATA_BIND_REGEX.test(value);

export const isDynamicValue = cappedMemoize(isDynamicValuePlain, 1500);

function getDynamicStringSegments(dynamicString: string): string[] {
  const stringSegments: string[] = [];
  const length = dynamicString.length;
  let currentSegmentStart = 0;
  let braceDepth = 0;
  let inBinding = false;

  for (let i = 0; i < length - 1; i++) {
    const char = dynamicString[i];
    const nextChar = dynamicString[i + 1];

    if (char === '{' && nextChar === '{' && !inBinding) {
      // found start of binding - push any preceding text
      if (i > currentSegmentStart) {
        stringSegments.push(dynamicString.substring(currentSegmentStart, i));
      }
      inBinding = true;
      braceDepth = 2;
      currentSegmentStart = i;
      i++; // skip next char since we processed it
    } else if (inBinding) {
      if (char === '{') {
        braceDepth++;
      } else if (char === '}') {
        braceDepth--;
        if (braceDepth === 1 && nextChar === '}') {
          // found end of binding
          stringSegments.push(dynamicString.substring(currentSegmentStart, i + 2));
          inBinding = false;
          braceDepth = 0;
          currentSegmentStart = i + 2;
          i++; // skip next char
        }
      }
    }
  }

  // handle remaining text after last binding
  if (currentSegmentStart < length) {
    stringSegments.push(dynamicString.substring(currentSegmentStart));
  }

  // if no valid bindings found, return original string
  if (stringSegments.length === 0) {
    return [dynamicString];
  }
  return stringSegments;
}

export const addParenthesis = (segment: string) => {
  return `(${segment})`;
};

export const removeParenthesis = (segment: string) => {
  return segment.substring(1, segment.length - 1);
};

export const getDynamicBindings = (dynamicString: string) => {
  // Protect against bad string parse
  if (!dynamicString || !isString(dynamicString)) {
    return { stringSegments: [], jsSnippets: [] };
  }
  const sanitisedString = dynamicString.trim();

  // Get the {{binding}} bound values
  const stringSegments = getDynamicStringSegments(sanitisedString);

  const paths = new Array(stringSegments.length);

  for (let i = 0, len = stringSegments.length; i < len; i++) {
    const segment = stringSegments[i];
    const segmentLength = segment.length;

    // check if segment starts and ends with {{ }}
    if (
      segmentLength >= 4 &&
      segment[0] === '{' &&
      segment[1] === '{' &&
      segment[segmentLength - 2] === '}' &&
      segment[segmentLength - 1] === '}'
    ) {
      // wrap the js thing inside ( ) to make it a valid js expression
      paths[i] = addParenthesis(segment.substring(2, segmentLength - 2));
    } else {
      paths[i] = '';
    }
  }

  return { stringSegments, jsSnippets: paths };
};
const NEW_LINE_REGEX = /[\n\r]+/g;

// Reference: https://github.com/appsmithorg/appsmith/blob/release/app/client/src/components/propertyControls/utils.ts#L8
const stringToJSPlain = (string: string): string => {
  const { jsSnippets, stringSegments } = getDynamicBindings(string);

  const segments = new Array(stringSegments.length);

  for (let i = 0, len = stringSegments.length; i < len; i++) {
    const jsSnippet = jsSnippets[i];

    if (jsSnippet && jsSnippet.length > 0) {
      // remove "\n" from the expression before evaluating
      segments[i] = jsSnippet.replaceAll(NEW_LINE_REGEX, '');
    } else {
      // Escape single quotes in the segment to prevent syntax errors
      const segment = stringSegments[i];
      segments[i] = `'${segment.replace(/'/g, "\\'")}'`;
    }
  }

  return segments.join(' + ');
};

export const stringToJS = cappedMemoize(stringToJSPlain, 1500);
